import * as React from 'react'
import { observer } from 'mobx-react'
import { CursorPageQuery } from 'types/definitions'
import { NewUserTransactionViewModel } from 'payouts/admin/review/NewUserTransactionViewModel'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetTitle,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetSection,
} from 'components/ModalSheet'
import {
  Form,
  FormRow,
  FormGroupContainer,
  NumericInputContainer,
  TextAreaContainer,
} from 'components/inputs'
import {
  CustomSelect,
  SingleItemSelectedDefault,
  useCustomSelectStateFromItemSource,
  ItemSource,
} from 'components/inputs/CustomSelect'
import Avatar from 'components/Avatar'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import Button from 'components/Button'
import { ToggleGroup, ToggleItem } from 'components/ToggleGroup'
import ErrorBlock from 'components/ErrorBlock'
import { TransactionType } from 'payouts/TransactionType'
import { formatTransactionReason } from 'payouts/formatters/transaction-formatters'
import { ConfirmNewTransactionModal } from './ConfirmNewTransactionModal'
import { CancellationToken } from '../../../../../infra/cancellation'

interface NewUserTransactionModalProps {
  vm: NewUserTransactionViewModel
}

/**
 * The NewUserTransactionModal
 */
export default observer(function NewUserTransactionModal({
  vm,
}: NewUserTransactionModalProps) {
  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.createTransaction.pending ? undefined : vm.close}
      disableCloseOnEsc
    >
      <>
        <ModalSheetHeader withCloseButton bordered>
          <ModalSheetTitle emphasized>Add transaction</ModalSheetTitle>
        </ModalSheetHeader>
        <ModalSheetContent>
          <ModalSheetSection spaced>
            <Form onSubmit={vm.createTransaction}>
              <FormRow>
                <WorkspaceSelector vm={vm} />
              </FormRow>
              <FormRow>
                <RecipientSelector vm={vm} />
              </FormRow>
              <FormRow>
                <AmountInput vm={vm} />
              </FormRow>
              <FormRow>
                <TransactionTypeSelector vm={vm} />
              </FormRow>
              <FormRow>
                <TransactionReasonSelector vm={vm} />
              </FormRow>
              <FormRow>
                <MemoInput vm={vm} />
              </FormRow>
            </Form>
          </ModalSheetSection>
        </ModalSheetContent>
        {!vm.validation.isValid && (
          <ErrorBlock tackOn="bottom" error="There are validation errors." />
        )}
        {vm.createTransaction.error && (
          <ErrorBlock tackOn="bottom" error={vm.createTransaction.error} />
        )}
        <ModalSheetFooter>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button disabled={vm.createTransaction.pending} onClick={vm.close}>
              Close
            </Button>
            <Button
              disabled={vm.createTransaction.pending}
              loading={vm.createTransaction.pending}
              type="primary"
              onClick={vm.createTransaction}
            >
              Add Transaction
            </Button>
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
        <ConfirmNewTransactionModal vm={vm.confirmationVM} />
      </>
    </ModalSheet>
  )
})

/**
 * Workspace Selector
 */
const WorkspaceSelector = observer(function WorkspaceSelector({
  vm,
}: {
  vm: NewUserTransactionViewModel
}) {
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource: vm.searchWorkspaces,
  })
  const error = vm.validation.getError('selectedWorkspace')
  return (
    <FormGroupContainer
      label="Workspace"
      error={error}
      help="The transaction will be attributed to this workspace"
    >
      <CustomSelect
        {...selectProps}
        status={error ? 'danger' : 'default'}
        placeholder="Select a Workspace"
        itemToString={(v) => String(v?.name)}
        disabled={vm.disabled}
        value={vm.selectedWorkspace ?? undefined}
        onChange={vm.selectWorkspace}
        multi={false}
        data-test="new-transaction-recipient-selector"
        renderItem={(itemProps) => (
          <SingleItemSelectedDefault
            {...itemProps}
            visual={
              <Avatar
                size={24}
                subject={{
                  displayName: itemProps.item.name,
                  avatar: itemProps.item.branding.logos.icon,
                }}
              />
            }
          />
        )}
        renderSearchItem={({ item, itemToString, ...itemProps }) => (
          <SearchResultItem
            {...itemProps}
            visual={
              <Avatar
                size={24}
                subject={{
                  avatar: item.branding.logos.icon,
                  displayName: item.name,
                }}
              />
            }
          >
            {item.name}
          </SearchResultItem>
        )}
      />
    </FormGroupContainer>
  )
})

/**
 * Recipient Selector
 */
const RecipientSelector = observer(function RecipientSelector({
  vm,
}: {
  vm: NewUserTransactionViewModel
}) {
  const itemSource = React.useMemo(
    () =>
      (
        searchText: string,
        pagination: CursorPageQuery,
        ct: CancellationToken,
      ) =>
        vm.searchMembers(searchText, pagination, ct),
    [vm.selectedWorkspace],
  )

  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
    debounce: 300,
  })
  const error = vm.validation.getError('selectedMember')
  return (
    <FormGroupContainer
      label="Recipient"
      error={error}
      help="The user that the transaction affects; must be a member of the selected workspace"
    >
      <CustomSelect
        {...selectProps}
        status={error ? 'danger' : 'default'}
        placeholder="Select a Workspace Member"
        itemToString={(v) => String(v?.displayName)}
        disabled={vm.disabled || !vm.selectedWorkspace}
        value={vm.selectedMember ?? undefined}
        onChange={vm.selectMember}
        multi={false}
        data-test="new-transaction-recipient-selector"
        renderItem={(itemProps) => (
          <SingleItemSelectedDefault
            {...itemProps}
            visual={<Avatar size={24} subject={itemProps.item} />}
          />
        )}
        renderSearchItem={({ item, itemToString, ...itemProps }) => (
          <SearchResultItem
            {...itemProps}
            visual={<Avatar size={24} subject={item} />}
          >
            {item.displayName || `(User ID: ${item.user.publicId})`}
          </SearchResultItem>
        )}
      />
    </FormGroupContainer>
  )
})

/**
 * Transaction Type Selector
 */
const TransactionTypeSelector = observer(function TransactionTypeSelector({
  vm,
}: {
  vm: NewUserTransactionViewModel
}) {
  const selectPayment = React.useCallback(
    () => vm.selectType(TransactionType.Payment),
    [vm],
  )
  const selectAbatement = React.useCallback(
    () => vm.selectType(TransactionType.Abatement),
    [vm],
  )
  return (
    <FormGroupContainer label="Type">
      <ToggleGroup stretch>
        <ToggleItem
          disabled={vm.disabled}
          toggled={vm.transactionType === TransactionType.Payment}
          onChange={selectPayment}
        >
          Payment
        </ToggleItem>
        <ToggleItem
          disabled={vm.disabled}
          toggled={vm.transactionType === TransactionType.Abatement}
          onChange={selectAbatement}
        >
          Abatement
        </ToggleItem>
      </ToggleGroup>
    </FormGroupContainer>
  )
})

/**
 * Transaction Reason Selector
 */
const TransactionReasonSelector = observer(function TransactionReasonSelector({
  vm,
}: {
  vm: NewUserTransactionViewModel
}) {
  const error = vm.validation.getError('reason')
  const itemSource = React.useMemo(
    () =>
      ItemSource.static(vm.availableReasons, (item, text) =>
        formatTransactionReason(item)
          .toLowerCase()
          .includes(text.toLowerCase()),
      ),
    [vm.availableReasons],
  )
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })
  return (
    <FormGroupContainer
      label="Reason"
      error={error}
      help="Specify a reason for this transaction"
    >
      <CustomSelect
        {...selectProps}
        status={error ? 'danger' : 'default'}
        placeholder="Select a reason"
        itemToString={(v) => (v ? formatTransactionReason(v) : '')}
        disabled={vm.disabled}
        value={vm.reason ?? undefined}
        onChange={vm.selectReason}
        multi={false}
        data-test="new-transaction-reason-selector"
      />
    </FormGroupContainer>
  )
})

/**
 * Amount input.
 */
const AmountInput = observer(function AmountInput({
  vm,
}: {
  vm: NewUserTransactionViewModel
}) {
  const error = vm.validation.getError('amount')
  return (
    <NumericInputContainer
      label="Amount"
      placeholder="The transaction amount"
      help="The positive transaction amount"
      currencyCode="usd"
      model={vm}
      field="amount"
      precision={2}
      disabled={vm.disabled}
      status={error ? 'danger' : 'default'}
      error={error}
    />
  )
})

/**
 * Memo input.
 */
const MemoInput = observer(function MemoInput({
  vm,
}: {
  vm: NewUserTransactionViewModel
}) {
  const error = vm.validation.getError('memo')
  return (
    <TextAreaContainer
      label="Memo"
      placeholder="Optional description for the transaction"
      model={vm}
      disabled={vm.disabled}
      status={error ? 'danger' : 'default'}
      field="memo"
      error={error}
      help="Max 280 characters"
    />
  )
})
